import React, { useCallback, useMemo, useState } from "react";
import { RefreshCw, Search } from "lucide-react";
import { SupervisorTeamHomeworkAssignment } from "../api/contracts";
import { clinicianPortalApi } from "../api/clinicianPortalApi";
import {
  ActivityTypePill,
  HomeworkStatusPill,
  PortalDataTable,
  PortalTableColumn,
  RelationshipPillList,
} from "../components/DataDisplay";
import {
  EmptyState,
  ErrorState,
  LoadingState,
} from "../components/FeedbackStates";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import { TeamTabs } from "../components/PortalNavigation";
import { PortalLink } from "../components/PortalLink";
import { SingleSelectPillField } from "../components/SingleSelectPillField";
import { TeamClinicianFilter } from "../components/TeamClinicianFilter";
import {
  TeamClinicFilter,
  TeamClinicUnavailableState,
} from "../components/TeamClinicFilter";
import { useClinicianAssistantTeamScopeFilters } from "../components/ClinicianAssistantTrigger";
import { teamHomeworkDetailPath } from "../config/navigation";
import {
  getActivityTypePillToken,
  getHomeworkCompletionStatusPillToken,
} from "../config/palette";
import { usePortalData } from "../hooks/usePortalData";
import {
  navigateWithinPortal,
  withClinicianCode,
} from "../routing/portalRoutes";
import { formatPortalDate } from "../utils/dateTime";
import {
  comparePortalDate,
  comparePortalNumber,
  comparePortalText,
  PortalSortDirection,
  stablePortalSort,
} from "../utils/tableSort";
import {
  uniqueSupervisorTeamClients,
  uniqueSupervisorTeamMembers,
  useTeamClinicScope,
  withTeamClinicScope,
} from "../utils/teamClinicScope";

type TeamHomeworkSortKey =
  | "client"
  | "clinic"
  | "homeworkName"
  | "clinician"
  | "activityType"
  | "completionStatus"
  | "dueDate"
  | "assignedDate"
  | "points"
  | "coins";

function initialFilterValue(
  key: "therapistId" | "patientId" | "activityType" | "status",
): string {
  if (typeof window === "undefined") return "";
  return new URLSearchParams(window.location.search).get(key)?.trim() ?? "";
}

function teamHomeworkColumns(
  clinicianCode: string,
  options: {
    clinicId: string;
    showClinician: boolean;
    sortProps: (
      key: TeamHomeworkSortKey,
    ) => Pick<
      PortalTableColumn<SupervisorTeamHomeworkAssignment>,
      "sortDirection" | "onSort"
    >;
  },
): readonly PortalTableColumn<SupervisorTeamHomeworkAssignment>[] {
  return [
    {
      key: "client",
      header: "Client",
      initialWidth: 180,
      minWidth: 150,
      ...options.sortProps("client"),
      render: (assignment) => (
        <span className="cp-client-summary">
          <strong>{assignment.nickname || "Unnamed client"}</strong>
          <small>{assignment.accessCode}</small>
        </span>
      ),
    },
    {
      key: "clinic",
      header: "Clinic",
      initialWidth: 210,
      minWidth: 170,
      ...options.sortProps("clinic"),
      render: (assignment) =>
        assignment.clinicName ? (
          <RelationshipPillList names={[assignment.clinicName]} />
        ) : (
          <span className="cp-empty-value">Unassigned</span>
        ),
    },
    {
      key: "homeworkName",
      header: "Homework Activity",
      initialWidth: 240,
      minWidth: 210,
      ...options.sortProps("homeworkName"),
      render: (assignment) => (
        <PortalLink
          className="cp-homework-name cp-homework-activity-name"
          path={withTeamClinicScope(
            teamHomeworkDetailPath(assignment.patientId, assignment.id),
            options.clinicId,
          )}
          clinicianCode={clinicianCode}
        >
          {assignment.homeworkName || "–"}
        </PortalLink>
      ),
    },
    ...(options.showClinician
      ? [
          {
            key: "clinician",
            header: "Clinician",
            initialWidth: 190,
            minWidth: 160,
            ...options.sortProps("clinician"),
            render: (assignment: SupervisorTeamHomeworkAssignment) => (
              <RelationshipPillList names={[assignment.clinicianName]} />
            ),
          },
        ]
      : []),
    {
      key: "activityType",
      header: "Activity Type",
      initialWidth: 190,
      minWidth: 160,
      ...options.sortProps("activityType"),
      render: (assignment) => (
        <ActivityTypePill activityType={assignment.activityType} />
      ),
    },
    {
      key: "completionStatus",
      header: "Status",
      initialWidth: 165,
      minWidth: 155,
      ...options.sortProps("completionStatus"),
      render: (assignment) => (
        <HomeworkStatusPill status={assignment.completionStatus} />
      ),
    },
    {
      key: "dueDate",
      header: "Due Date",
      initialWidth: 110,
      minWidth: 105,
      ...options.sortProps("dueDate"),
      render: (assignment) => formatPortalDate(assignment.dueDate),
    },
    {
      key: "assignedDate",
      header: "Assigned Date",
      initialWidth: 120,
      minWidth: 115,
      ...options.sortProps("assignedDate"),
      render: (assignment) => formatPortalDate(assignment.assignedDate),
    },
    {
      key: "points",
      header: "Points",
      initialWidth: 80,
      minWidth: 80,
      cellClassName: "cp-table-cell--compact",
      ...options.sortProps("points"),
      render: (assignment) => assignment.points,
    },
    {
      key: "coins",
      header: "Coins",
      initialWidth: 80,
      minWidth: 80,
      resizable: false,
      cellClassName: "cp-table-cell--compact",
      ...options.sortProps("coins"),
      render: (assignment) => assignment.coins,
    },
  ];
}

export function TeamHomeworkPage({ clinicianCode }: { clinicianCode: string }) {
  const { clinicId, setClinicId } = useTeamClinicScope();
  const [search, setSearch] = useState("");
  const [therapistId, setTherapistId] = useState(() =>
    initialFilterValue("therapistId"),
  );
  const [patientId, setPatientId] = useState(() =>
    initialFilterValue("patientId"),
  );
  const [activityType, setActivityType] = useState(() =>
    initialFilterValue("activityType"),
  );
  const [status, setStatus] = useState(() => initialFilterValue("status"));
  const [sortKey, setSortKey] = useState<TeamHomeworkSortKey | null>(null);
  const [sortDirection, setSortDirection] =
    useState<PortalSortDirection>("ascending");
  const request = useCallback(
    (signal: AbortSignal) =>
      clinicianPortalApi.getSupervisorTeamHomework(
        clinicianCode,
        clinicId,
        signal,
      ),
    [clinicId, clinicianCode],
  );
  const state = usePortalData(request);
  const data =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const available = Boolean(
    data?.found && data.authorized && data.therapist?.role === "supervisor",
  );
  const clinicOptions = data?.clinicOptions ?? data?.clinics ?? [];
  const staleClinic = Boolean(
    data && clinicId && !clinicOptions.some((clinic) => clinic.id === clinicId),
  );
  const clinicianOptions = useMemo(() => {
    if (!data) return [];
    return uniqueSupervisorTeamMembers(data.teamMembers).sort((left, right) =>
      left.therapistName.localeCompare(right.therapistName),
    );
  }, [data]);
  const effectiveTherapistId = clinicianOptions.some(
    (member) => member.id === therapistId,
  )
    ? therapistId
    : "";
  useClinicianAssistantTeamScopeFilters({
    clinicId,
    therapistId: effectiveTherapistId,
  });
  const assignments = useMemo(() => {
    const byId = new Map<string, SupervisorTeamHomeworkAssignment>();
    for (const assignment of data?.assignments ?? []) {
      if (!byId.has(assignment.id)) byId.set(assignment.id, assignment);
    }
    return [...byId.values()];
  }, [data?.assignments]);
  const statuses = useMemo(
    () =>
      Array.from(
        new Set(
          assignments
            .map((assignment) => assignment.completionStatus.trim())
            .filter(Boolean),
        ),
      ).sort((a, b) => a.localeCompare(b)),
    [assignments],
  );
  const effectiveStatus = statuses.includes(status) ? status : "";
  const activityTypes = useMemo(
    () =>
      Array.from(
        new Set(
          assignments
            .map((assignment) => assignment.activityType.trim())
            .filter(Boolean),
        ),
      ).sort((a, b) => a.localeCompare(b)),
    [assignments],
  );
  const effectiveActivityType = activityTypes.includes(activityType)
    ? activityType
    : "";
  const activityTypeOptions = useMemo(
    () => [
      { id: "all-activity-types", value: "", label: "All Activity Types" },
      ...activityTypes.map((option) => ({
        id: option,
        value: option,
        label: option,
      })),
    ],
    [activityTypes],
  );
  const statusOptions = useMemo(
    () => [
      { id: "all-statuses", value: "", label: "All Statuses" },
      ...statuses.map((option) => ({
        id: option,
        value: option,
        label: option,
      })),
    ],
    [statuses],
  );
  const clientOptions = useMemo(() => {
    if (!data) return [];
    return uniqueSupervisorTeamClients(data.clients).filter((client) => {
      if (
        effectiveTherapistId &&
        !assignments.some(
          (assignment) =>
            assignment.patientId === client.id &&
            assignment.clinicianId === effectiveTherapistId,
        )
      ) {
        return false;
      }
      return true;
    });
  }, [assignments, data, effectiveTherapistId]);
  const effectivePatientId = clientOptions.some(
    (client) => client.id === patientId,
  )
    ? patientId
    : "";
  const filteredAssignments = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return assignments.filter((assignment) => {
      if (effectivePatientId && assignment.patientId !== effectivePatientId) {
        return false;
      }
      if (
        effectiveActivityType &&
        assignment.activityType.trim() !== effectiveActivityType
      ) {
        return false;
      }
      if (effectiveStatus && assignment.completionStatus !== effectiveStatus) {
        return false;
      }
      if (!query) return true;
      return [
        assignment.nickname,
        assignment.accessCode,
        assignment.homeworkName,
        assignment.activityType,
        assignment.completionStatus,
        assignment.activitySteps,
        assignment.clinicName,
      ]
        .join(" ")
        .toLocaleLowerCase()
        .includes(query);
    });
  }, [
    assignments,
    effectiveActivityType,
    effectivePatientId,
    effectiveStatus,
    search,
  ]);
  const clinicianAssignmentCounts = useMemo(() => {
    const counts = new Map<string, number>();
    clinicianOptions.forEach((member) => counts.set(member.id, 0));
    filteredAssignments.forEach((assignment) => {
      if (counts.has(assignment.clinicianId)) {
        counts.set(
          assignment.clinicianId,
          (counts.get(assignment.clinicianId) ?? 0) + 1,
        );
      }
    });
    return counts;
  }, [clinicianOptions, filteredAssignments]);
  const visibleAssignments = useMemo(
    () =>
      effectiveTherapistId
        ? filteredAssignments.filter(
            (assignment) => assignment.clinicianId === effectiveTherapistId,
          )
        : filteredAssignments,
    [effectiveTherapistId, filteredAssignments],
  );
  const sortedAssignments = useMemo(() => {
    if (!sortKey) return visibleAssignments;
    return stablePortalSort(visibleAssignments, (left, right) => {
      switch (sortKey) {
        case "client":
          return (
            comparePortalText(left.nickname, right.nickname, sortDirection) ||
            comparePortalText(left.accessCode, right.accessCode, sortDirection)
          );
        case "clinician":
          return comparePortalText(
            left.clinicianName,
            right.clinicianName,
            sortDirection,
          );
        case "clinic":
          return comparePortalText(
            left.clinicName,
            right.clinicName,
            sortDirection,
          );
        case "dueDate":
        case "assignedDate":
          return comparePortalDate(
            left[sortKey],
            right[sortKey],
            sortDirection,
          );
        case "points":
        case "coins":
          return comparePortalNumber(
            left[sortKey],
            right[sortKey],
            sortDirection,
          );
        default:
          return comparePortalText(
            left[sortKey],
            right[sortKey],
            sortDirection,
          );
      }
    });
  }, [sortDirection, sortKey, visibleAssignments]);
  const updateSort = (nextSortKey: TeamHomeworkSortKey): void => {
    setSortDirection(
      nextSortKey === sortKey && sortDirection === "ascending"
        ? "descending"
        : "ascending",
    );
    setSortKey(nextSortKey);
  };
  const sortProps = (key: TeamHomeworkSortKey) => ({
    sortDirection: sortKey === key ? sortDirection : ("none" as const),
    onSort: () => updateSort(key),
  });
  const columns = teamHomeworkColumns(clinicianCode, {
    clinicId,
    showClinician: !effectiveTherapistId,
    sortProps,
  });

  return (
    <ClinicianPortalLayout
      activeGlobalKey="team"
      clinicianCode={clinicianCode}
      therapistRole={data?.therapist?.role}
    >
      <PortalHeader
        title="Team Homework Activities"
        clinicianName={available ? data?.therapist?.therapistName : undefined}
        clinicianNameLoading={state.status === "loading"}
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={[
              { label: "Home", path: "/clinician-portal" },
              {
                label: "Team",
                path: withTeamClinicScope("/clinician-portal/team", clinicId),
              },
              { label: "Homework Activities" },
            ]}
          />
        }
      />
      <div className="cp-page-content cp-page-content--wide cp-page-content--sticky-list">
        <TeamTabs
          clinicianCode={clinicianCode}
          activeKey="homework"
          clinicId={clinicId}
        />
        {state.status === "loading" ? (
          <LoadingState label="Loading team homework activities" />
        ) : null}
        {state.status === "error" ? (
          <ErrorState error={state.error} onRetry={state.refresh} />
        ) : null}
        {data && !available ? (
          <ErrorState
            error={new Error("Team homework activities are unavailable")}
          />
        ) : null}
        {available && data && staleClinic ? (
          <TeamClinicUnavailableState
            onViewAllClinics={() => setClinicId("")}
          />
        ) : null}
        {available && data && !staleClinic ? (
          <section aria-label="Team Homework Activities">
            <div className="cp-content-header">
              <div>
                <p>
                  Review homework assigned by you and other clinicians to
                  clients within your authorized clinics. This view is
                  read-only.
                </p>
              </div>
              <strong className="cp-record-count">
                {visibleAssignments.length}{" "}
                {visibleAssignments.length === 1
                  ? "homework assignment"
                  : "homework assignments"}
              </strong>
            </div>
            <div
              className="cp-team-homework-filters"
              aria-label="Team Homework Activities filters"
            >
              <TeamClinicianFilter
                value={effectiveTherapistId}
                allCount={filteredAssignments.length}
                options={clinicianOptions.map((member) => ({
                  id: member.id,
                  name: member.therapistName,
                  isSupervisor: member.memberships.some(
                    (membership) => membership.membershipRole === "supervisor",
                  ),
                  isCurrentUser: member.isCurrentUser,
                  count: clinicianAssignmentCounts.get(member.id) ?? 0,
                }))}
                onChange={(nextTherapistId) => {
                  setTherapistId(nextTherapistId);
                  setPatientId("");
                }}
                countLabel="homework assignment"
                countLabelPlural="homework assignments"
                groupLabel="Clinician homework assignments"
              />
              <div className="cp-list-toolbar cp-list-toolbar--homework cp-team-homework-toolbar">
                <div className="cp-list-toolbar__filters cp-list-toolbar__filters--homework">
                  <label className="cp-search-control">
                    <Search aria-hidden="true" />
                    <span className="cp-visually-hidden">Search homework</span>
                    <input
                      type="search"
                      value={search}
                      placeholder="Search homework"
                      onChange={(event) => setSearch(event.target.value)}
                    />
                  </label>
                  <TeamClinicFilter
                    clinicOptions={clinicOptions}
                    value={clinicId}
                    compact
                    onChange={(nextClinicId) => {
                      setClinicId(nextClinicId);
                      setTherapistId("");
                      setPatientId("");
                    }}
                  />
                  <label className="cp-homework-list-filter">
                    <span className="cp-visually-hidden">Client</span>
                    <select
                      className="cp-form-input"
                      aria-label="Client"
                      value={effectivePatientId}
                      onChange={(event) =>
                        setPatientId(event.currentTarget.value)
                      }
                    >
                      <option value="">All Clients</option>
                      {clientOptions.map((client) => (
                        <option key={client.id} value={client.id}>
                          {`${client.nickname || "Unnamed client"} (${client.accessCode})`}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="cp-homework-list-filter cp-homework-list-filter--activity-type">
                    <span className="cp-visually-hidden">Activity Type</span>
                    <SingleSelectPillField
                      id="team-homework-activity-type-filter"
                      value={effectiveActivityType}
                      options={activityTypeOptions}
                      ariaLabel="Activity Type"
                      placeholder="All Activity Types"
                      searchPlaceholder="Search Activity Types"
                      emptyMessage="No Activity Types found"
                      resolveColor={(option) =>
                        option.value
                          ? getActivityTypePillToken(option.label)
                          : "neutral"
                      }
                      onChange={setActivityType}
                    />
                  </label>
                  <label className="cp-homework-list-filter cp-homework-list-filter--status">
                    <span className="cp-visually-hidden">Status</span>
                    <SingleSelectPillField
                      id="team-homework-status-filter"
                      value={effectiveStatus}
                      options={statusOptions}
                      ariaLabel="Status"
                      placeholder="All Statuses"
                      searchPlaceholder="Search Statuses"
                      emptyMessage="No Statuses found"
                      resolveColor={(option) =>
                        option.value
                          ? getHomeworkCompletionStatusPillToken(option.value)
                          : "neutral"
                      }
                      onChange={setStatus}
                    />
                  </label>
                </div>
                <div className="cp-list-toolbar__actions">
                  <button
                    type="button"
                    className="cp-button cp-button--secondary"
                    onClick={state.refresh}
                    disabled={state.status === "refreshing"}
                  >
                    <RefreshCw aria-hidden="true" />
                    {state.status === "refreshing" ? "Refreshing…" : "Refresh"}
                  </button>
                </div>
              </div>
            </div>
            {sortedAssignments.length ? (
              <PortalDataTable
                caption="Team Homework Activities"
                columns={columns}
                records={sortedAssignments}
                getRecordKey={(assignment) => assignment.id}
                getRecordOpenLabel={(assignment) =>
                  `View ${assignment.homeworkName || "homework assignment"}`
                }
                onRecordOpen={(assignment) =>
                  navigateWithinPortal(
                    withClinicianCode(
                      withTeamClinicScope(
                        teamHomeworkDetailPath(
                          assignment.patientId,
                          assignment.id,
                        ),
                        clinicId,
                      ),
                      clinicianCode,
                    ),
                  )
                }
              />
            ) : (
              <EmptyState
                title={
                  clinicId
                    ? "No homework assignments were found for the selected clinic."
                    : "No team homework found"
                }
                description="No homework assignments match the current search and selected clinician, clinic, client, Activity Type, and Status filters."
              />
            )}
          </section>
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
